import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { TeamSnapshotWithUser } from '@/api/type-response'
import { Button, Loading, Pagination, Trans } from '@/components'
import { SnapStatus } from '@/models/snapshot'
import { useTeamStore } from '@/stores/team'
import { formatBytes } from '@/utils/formatter'

import { SnapshotFilters } from '../components/SnapshotFilters'
import { SnapshotStatCard, StatCountValue } from '../components/SnapshotStatCard'
import { SnapshotTable } from '../components/SnapshotTable'
import { SnapshotViewSwitcher } from '../components/SnapshotViewSwitcher'
import { PAGE_SIZE } from '../constants'
import { useSnapshotList } from '../hooks/useSnapshotList'
import { useSnapshotOperations } from '../hooks/useSnapshotOperations'
import { useTeamSnapshotStats } from '../hooks/useTeamSnapshotStats'

export function AdminSnapshotView() {
  const { t } = useTranslation()
  const [selectedMembers, setSelectedMembers] = useState<string[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<(string | number)[]>(['all'])
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const teamStore = useTeamStore()
  const teamTokenData = teamStore.teamTokenData
  // Fetch team snapshot statistics (team shared + all members)
  const {
    teamShared,
    allMembers,
    refetch: refetchQuota,
  } = useTeamSnapshotStats({
    teamResID: teamTokenData?.teamResID,
  })

  const { snapshots, loading, page, total, fetchSnapshots } = useSnapshotList<TeamSnapshotWithUser>(
    {
      type: 'team-member',
      teamResID: teamTokenData?.teamResID,
      selectedMembers,
      selectedStatuses,
    },
  )

  const { importToShared, bulkImportToShared } = useSnapshotOperations({
    teamResID: teamTokenData?.teamResID,
    onSuccess: () => {
      fetchSnapshots()
      refetchQuota()
    },
  })

  // Check if selected snapshots can be deleted or shared (only Available status)
  const selectedSnapshotsInfo = useMemo(() => {
    const selected = snapshots.filter((s) => selectedIds.includes(s.snapshot.resID))
    const allAvailable = selected.every((s) => s.snapshot.status === SnapStatus.Finished)
    const nonAvailableCount = selected.filter(
      (s) => s.snapshot.status !== SnapStatus.Finished,
    ).length

    return {
      count: selected.length,
      canDelete: allAvailable,
      canShareToTeam: allAvailable,
      nonAvailableCount,
    }
  }, [selectedIds, snapshots])

  const handleSelectionChange = (ids: string[]) => {
    setSelectedIds(ids)
  }

  const selectedSnapshots = useMemo(
    () => snapshots.filter((snap) => selectedIds.includes(snap.snapshot.resID)),
    [snapshots, selectedIds],
  )

  const handleBulkShareToTeam = async () => {
    await bulkImportToShared(selectedSnapshots)
    setSelectedIds([])
  }

  const handleBulkEdit = (action: string) => {
    if (action === 'shareToTeam') {
      handleBulkShareToTeam()
    }
  }

  const handleStatusChange = (value: (string | number)[] | string | undefined) => {
    if (Array.isArray(value)) {
      setSelectedStatuses(value)
    } else if (typeof value === 'string') {
      setSelectedStatuses([value])
    } else {
      setSelectedStatuses(['all'])
    }
  }

  return (
    <div className="flex flex-col gap-4 h-full overflow-y-auto">
      {/* Section 1: Stat Cards */}
      <div className="card bg-panel-white p-6 grid grid-cols-4 gap-4">
        <SnapshotStatCard
          title={
            <Trans i18nKey="teamSnapshot.admin.sharedStorageCard">Team Snapshots Storage</Trans>
          }
          value={formatBytes(teamShared.usageBytes) || '0 B'}
          action={
            <Link to="/team/snapshots/shared" className="text-btn-secondary t-sb">
              <Trans i18nKey="teamSnapshot.admin.manageLink">Manage</Trans>
            </Link>
          }
        />
        <SnapshotStatCard
          title={<Trans i18nKey="teamSnapshot.admin.sharedNumberCard">Team Snapshots Number</Trans>}
          value={<StatCountValue count={teamShared.count} quota={teamShared.slotQuota} />}
        />
        <SnapshotStatCard
          title={
            <Trans i18nKey="teamSnapshot.admin.allMembersStorageCard">
              Team Snapshots Storage (All Members)
            </Trans>
          }
          value={formatBytes(allMembers.usageBytes) || '0 B'}
        />
        <SnapshotStatCard
          title={
            <Trans i18nKey="teamSnapshot.admin.allMembersNumberCard">
              Team Snapshots Number (All Members)
            </Trans>
          }
          value={<StatCountValue count={allMembers.count} quota={allMembers.slotQuota} />}
        />
      </div>

      <div className="card bg-panel-white p-8 flex flex-col gap-8 flex-1 overflow-y-auto">
        {/* Section 2: Title + ViewSwitcher */}
        <div className="border-b border-neutral-100 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h6">
                <Trans i18nKey="snapshot.title">Snapshots</Trans>
              </div>
              <SnapshotViewSwitcher />
            </div>
            <div>
              <Link to="/team/create">
                <Button type="secondary">
                  <Trans i18nKey="snapshot.createBtn">New Instance</Trans>
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Section 3: Filters + Table */}
        <div className="flex flex-col gap-4">
          {/* Filters */}
          <SnapshotFilters
            statusFilterType="dropdown"
            statusOptions={[
              { label: t('snapshot.type.available', 'Available'), value: 'available' },
              { label: t('snapshot.type.restorable', 'Restorable'), value: 'restorable' },
            ]}
            selectedStatuses={selectedStatuses}
            onStatusChange={handleStatusChange}
            showMemberFilter={true}
            selectedMembers={selectedMembers}
            onMembersChange={setSelectedMembers}
            selectedCount={selectedIds.length}
            bulkEditActions={[
              {
                label: t('teamSnapshot.bulkEdit.shareToTeam', 'Share to Team'),
                value: 'shareToTeam',
                enabled: selectedSnapshotsInfo.canShareToTeam,
                tooltip:
                  selectedSnapshotsInfo.nonAvailableCount > 0
                    ? 'Restorable snapshots cannot be shared to team'
                    : '',
              },
            ]}
            onBulkEdit={handleBulkEdit}
            bulkEditTooltip={
              selectedSnapshotsInfo.nonAvailableCount > 0
                ? 'Restorable snapshots cannot be shared to team'
                : ''
            }
          />

          {/* Table */}
          {loading ? (
            <div className="w-full flex items-center justify-center p-8">
              <Loading />
            </div>
          ) : (
            <>
              <SnapshotTable
                snapshots={snapshots}
                selectedIds={selectedIds}
                onSelectionChange={handleSelectionChange}
                variant="admin"
                showCheckbox={true}
                showOwner={true}
                detailLinkPath={(record) =>
                  `/team/snapshots/member-detail/${record.snapshot.resID}`
                }
                actionItems={(record) => {
                  const ownerName = record.user.user.nickname || record.user.user.name || '-'
                  return [
                    {
                      label: <Trans i18nKey="teamSnapshot.admin.actionImport">Share to team</Trans>,
                      onClick: () => importToShared(record, ownerName),
                      disabled: record.snapshot.status !== SnapStatus.Finished,
                    },
                  ]
                }}
              />
              <div className="flex justify-between items-center mt-4">
                <div className="t-sm text-secondary">
                  {selectedIds.length > 0 ? (
                    <Trans
                      i18nKey="team.admin.selected"
                      values={{ count: selectedIds.length, total }}
                    >
                      Selected {selectedIds.length} / {total}
                    </Trans>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
                <Pagination
                  size={PAGE_SIZE}
                  total={total}
                  current={page}
                  onChange={(newPage) => fetchSnapshots(newPage)}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
